import { unstable_composeClasses as composeClasses } from '@mui/base';
import { styled, useThemeProps, ClickAwayListener, InputBase } from '@mui/material';
import { Add, Remove } from '@ori-ui/icons';
import clsx from 'clsx';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import counterClasses, { getCounterUtilityClass } from './counterClasses';
import IconButton from '../IconButton';
import useCounter from '../useCounter';
import { capitalize, useForkCallback } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const getCounterSlots = ownerState => {
  const {
    readOnly,
    disabled,
    size,
    color,
    variant
  } = ownerState;
  return {
    root: ['root', disabled && 'disabled', readOnly && 'readOnly', `size${capitalize(size)}`, `color${capitalize(color)}`, `variant${capitalize(variant)}`, `${variant}${capitalize(color)}${capitalize(size)}`, `${variant}${capitalize(color)}`],
    input: ['input'],
    iconButton: ['iconButton'],
    increaseButton: ['increaseButton'],
    decreaseButton: ['decreaseButton']
  };
};
const useUtilityClasses = ownerState => {
  const {
    classes
  } = ownerState;
  const slots = getCounterSlots(ownerState);
  return composeClasses(slots, getCounterUtilityClass, classes);
};
const CounterRoot = styled('div', {
  name: 'MuiOriCounter',
  slot: 'Root',
  overridesResolver: ({
    ownerState
  }, styles) => [styles.root, ownerState.disabled && styles.disabled, ownerState.readOnly && styles.readOnly, styles[`size${capitalize(ownerState.size)}`], styles[`color${capitalize(ownerState.color)}`], styles[`variant${capitalize(ownerState.variant)}`], styles[`${ownerState.variant}${capitalize(ownerState.color)}${capitalize(ownerState.size)}`], styles[`${ownerState.variant}${capitalize(ownerState.color)}`]]
})(({
  theme,
  ownerState
}) => ({
  display: 'inline-flex',
  flexDirection: 'row',
  height: 30,
  gap: theme.spacing(2),
  ...(ownerState.expandOnFocus && {
    width: 25,
    overflow: 'hidden',
    transition: theme.transitions.create(['width', 'translate'], {
      duration: theme.transitions.duration.short
    }),
    ...(ownerState.focused && {
      width: 105
    })
  }),
  ...(ownerState.size === 'large' && {
    ...(ownerState.expandOnFocus && {
      width: 40,
      ...(ownerState.focused && {
        width: 120
      })
    })
  }),
  [`&.${counterClasses.disabled}`]: {}
}));
const Input = styled(InputBase, {
  name: 'MuiOriCounter',
  slot: 'Input',
  overridesResolver: (_, styles) => [styles.input]
})(({
  theme,
  ownerState
}) => ({
  margin: 0,
  padding: 0,
  minWidth: 25,
  width: 25,
  height: 26,
  transition: theme.transitions.create(['border'], {
    duration: theme.transitions.duration.short
  }),
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.shape.smallComponentRadius,
  alignSelf: 'center',
  fontSize: 16,
  ...(ownerState.variant === 'text' && {
    borderColor: 'transparent',
    ...(ownerState.focused && {
      borderColor: theme.palette.grey[200]
    })
  }),
  ...(ownerState.size === 'large' && {
    minWidth: 40,
    width: 40,
    height: 30
  }),
  ...(ownerState.readOnly && {
    border: 'none'
  }),
  ...(ownerState.focused && {
    borderColor: theme.palette.grey[400]
  }),
  '&:hover': {
    borderColor: theme.palette.grey[400],
    ...(ownerState.variant === 'text' && {
      borderColor: theme.palette.grey[200]
    }),
    // Reset on touch devices, it doesn't add specificity
    '@media (hover: none)': {
      borderColor: theme.palette.grey[200]
    }
  },
  [`& input`]: {
    fontSize: theme.typography.pxToRem(16),
    textAlign: 'center',
    padding: 0,
    ...(ownerState.readOnly && {
      cursor: 'default'
    })
  },
  [`.${counterClasses.disabled} &`]: {
    borderColor: theme.palette.action.disabled
  }
}));
const IconButtonRoot = styled(IconButton, {
  name: 'MuiOriCounter',
  slot: 'iconButton',
  overridesResolver: (_, styles) => [styles.iconButton]
})(({
  theme,
  ownerState
}) => ({
  width: 30,
  height: 30,
  [`&.${counterClasses.decreaseButton}`]: {
    transition: theme.transitions.create(['margin'], {
      duration: theme.transitions.duration.short
    }),
    ...(ownerState.expandOnFocus && {
      marginLeft: -40
    }),
    ...(ownerState.expandOnFocus && ownerState.focused && {
      marginLeft: 0
    })
  }
}));
export const Counter = /*#__PURE__*/forwardRef(function Counter(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiOriCounter'
  });
  const {
    maxValue = Number.POSITIVE_INFINITY,
    minValue = Number.NEGATIVE_INFINITY,
    disabled = false,
    className,
    readOnly = false,
    required,
    iconButton: IconButtonComponent = IconButtonRoot,
    input: InputComponent = Input,
    onChange,
    onFocus,
    onClickAway,
    expanded,
    component = 'div',
    expandOnFocus = false,
    variant = 'outlined',
    color = 'default',
    size = 'medium',
    IconButtonProps = {},
    IncreaseIconButtonProps = {},
    DecreaseIconButtonProps = {},
    InputProps: InputBaseProps = {},
    ...otherProps
  } = props;
  const [focused, setFocused] = useState(expanded ?? false);
  const {
    value,
    onDecrease,
    InputProps,
    onIncrease,
    handleChange
  } = useCounter(props);
  const ownerState = {
    ...props,
    disabled,
    readOnly,
    expandOnFocus,
    variant,
    color,
    focused,
    size
  };
  useEffect(() => {
    if (typeof expanded === 'boolean') {
      setFocused(expanded);
    }
  }, [expanded]);
  const handleClickAway = useCallback(() => {
    if (typeof expanded === 'boolean') {
      return;
    }
    setFocused(false);
  }, [expanded]);
  const handleFocus = useCallback(() => {
    if (typeof expanded === 'boolean') {
      return;
    }
    setFocused(true);
  }, [expanded]);
  const handleFocusForked = useForkCallback(handleFocus, onFocus);
  const handleClickAwayForked = useForkCallback(handleClickAway, onClickAway);
  const classes = useUtilityClasses(ownerState);
  const inputRef = useRef(null);
  return /*#__PURE__*/_jsx(ClickAwayListener, {
    onClickAway: handleClickAwayForked,
    children: /*#__PURE__*/_jsxs(CounterRoot, {
      ...otherProps,
      ref: ref,
      ownerState: ownerState,
      className: clsx(classes.root, className),
      as: component,
      onFocus: handleFocusForked,
      children: [/*#__PURE__*/_jsx(IconButtonComponent, {
        disableElevation: true,
        ownerState: ownerState,
        type: "button",
        size: "small",
        disabled: disabled || value <= minValue,
        className: clsx(classes.iconButton, classes.decreaseButton),
        color: color,
        variant: variant,
        ...IconButtonProps,
        ...DecreaseIconButtonProps,
        onClick: onDecrease,
        children: /*#__PURE__*/_jsx(Remove, {
          fontSize: "small"
        })
      }), /*#__PURE__*/_jsx(InputComponent, {
        inputRef: inputRef,
        ownerState: ownerState,
        readOnly: readOnly,
        required: required,
        value: value,
        disabled: disabled,
        className: classes.input,
        ...InputBaseProps,
        ...InputProps,
        onChange: handleChange
      }), /*#__PURE__*/_jsx(IconButtonComponent, {
        disableElevation: true,
        ownerState: ownerState,
        type: "button",
        size: "small",
        disabled: disabled || value >= maxValue,
        className: clsx(classes.iconButton, classes.increaseButton),
        color: color,
        variant: variant,
        ...IconButtonProps,
        ...IncreaseIconButtonProps,
        onClick: onIncrease,
        children: /*#__PURE__*/_jsx(Add, {
          fontSize: "small"
        })
      })]
    })
  });
});
export default Counter;